/**
 * The CenoX shell.
 *
 * One frame for the whole platform: a navy sidebar that holds CenoX's own
 * sections and, beneath them, the products this workspace can reach; a light
 * centre that is the workspace itself; and, where a page wants it, a right
 * panel saying what CenoX is connected to.
 *
 * The information architecture matters more than the styling. What this
 * replaced was a catalogue — a page of large product cards you had to get past
 * before you could do anything. Products belong in the furniture, reachable
 * and not in the way.
 *
 * Home and Chat are separate places. Work is a mode of Chat rather than a
 * place of its own — it has no backend yet — so its sidebar entry opens Chat
 * with Work selected, where it says plainly that it is not built.
 *
 * `CenoX Automation` is deliberately NOT a sidebar section. It is a standalone
 * product with its own route; a nav item here would say it lives inside this
 * shell. Actions that need it deep-link to `/automation` instead.
 *
 * The sidebar is navy in both themes (its own `navy` scale, which the dark
 * theme does not remap). The dark theme is the workspace's alone: `.cx-dark`
 * is set on this shell's root and nowhere else, so CenoX Business (AppShell)
 * cannot be reached by it.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell, Bot, Boxes, Check, ChevronDown, Code2, FileText, FlaskConical, Gauge, Home,
  Image, KeyRound, LayoutGrid, LogOut, Menu, MessageSquare, Mic, Monitor, Moon, Plus,
  Search, Settings, Sun, Video, Workflow, X,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { usePlatformProducts, type PlatformProduct } from '../lib/platformProducts';
import { useAiConversations } from '../lib/aiConversations';
import { NEXT_THEME, useCenoxTheme, type ThemePreference } from '../lib/cenoxTheme';
import { CenoxMark } from '../components/platform/CenoxMark';
import { ActivityList } from '../components/platform/ActivityList';
import { usePlatformMe } from '../pages/platform/PlatformAdminPage';
import { initials } from '../lib/format';
import { useOverlayFocus } from '../lib/useOverlayFocus';
import { cn } from '../components/ui';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  /** Whether this item is the current page — paths alone cannot tell Chat from Work. */
  active: (pathname: string, search: string) => boolean;
}

const isWork = (search: string): boolean => new URLSearchParams(search).get('mode') === 'work';
const under = (prefix: string) => (pathname: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);

const NAV: NavItem[] = [
  { to: '/home', label: 'Home', icon: Home, active: (p) => p === '/' || p === '/home' },
  { to: '/chat', label: 'Chat', icon: MessageSquare, active: (p, s) => under('/chat')(p) && !isWork(s) },
  { to: '/chat?mode=work', label: 'Work', icon: Workflow, active: (p, s) => under('/chat')(p) && isWork(s) },
  { to: '/ai/agents', label: 'Agents', icon: Bot, active: under('/ai/agents') },
  { to: '/ai/projects', label: 'Projects', icon: LayoutGrid, active: under('/ai/projects') },
  { to: '/ai/knowledge', label: 'Knowledge', icon: Boxes, active: under('/ai/knowledge') },
  { to: '/ai/files', label: 'Files', icon: FileText, active: under('/ai/files') },
  { to: '/ai/developer', label: 'Developer Tools', icon: Code2, active: under('/ai/developer') },
  { to: '/ai/images', label: 'Images', icon: Image, active: under('/ai/images') },
  { to: '/ai/audio', label: 'Audio', icon: Mic, active: under('/ai/audio') },
  { to: '/ai/video', label: 'Video', icon: Video, active: under('/ai/video') },
  { to: '/ai/evaluations', label: 'Evaluations', icon: FlaskConical, active: under('/ai/evaluations') },
  { to: '/ai/api-keys', label: 'API Keys', icon: KeyRound, active: under('/ai/api-keys') },
  { to: '/ai/usage', label: 'Usage', icon: Gauge, active: under('/ai/usage') },
  { to: '/ai/settings', label: 'Settings', icon: Settings, active: under('/ai/settings') },
];

const THEME_LABEL: Record<ThemePreference, string> = {
  system: 'Theme: match device',
  light: 'Theme: light',
  dark: 'Theme: dark',
};
const THEME_ICON: Record<ThemePreference, typeof Sun> = { system: Monitor, light: Sun, dark: Moon };

/** The navy sidebar's item styles, in one place. */
const navItem = (active: boolean): string =>
  cn(
    'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-400',
    active
      ? 'bg-cx-gradient font-medium text-white shadow-sm shadow-indigo-950/40'
      : 'text-navy-200 hover:bg-navy-800 hover:text-white',
  );

/**
 * A product's own dot colour while it is not open to this workspace. Products
 * not named here use the quiet navy dot; open is always emerald.
 */
const SOON_DOT: Record<string, string> = {
  verify: 'bg-blue-500',
};

/**
 * A product in the sidebar's ecosystem list.
 *
 * State comes from the server's own answer — the same call the right panel
 * reads, so the two can never disagree. A dot rather than a badge: this is a
 * list to move through, not a list to shop from.
 */
function EcosystemRow({ product, onNavigate }: { product: PlatformProduct; onNavigate?: () => void }): ReactNode {
  const open = product.availableAction === 'open';
  const comingSoon = product.catalogueStatus !== 'available';

  return (
    <Link
      to={`/${product.routeSlug}`}
      onClick={onNavigate}
      className={cn(
        'group flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm transition-colors hover:bg-navy-800',
        open ? 'text-navy-200 hover:text-white' : 'text-navy-400',
      )}
    >
      <span
        className={cn(
          'size-1.5 shrink-0 rounded-full',
          open ? 'bg-emerald-400' : (SOON_DOT[product.product] ?? 'bg-navy-600'),
        )}
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate">{product.name.replace(/^CenoX /, '')}</span>
      <span className="shrink-0 text-[10px] uppercase tracking-wide text-navy-400">
        {open ? 'Open' : comingSoon ? 'Soon' : 'Get access'}
      </span>
    </Link>
  );
}

/**
 * Which workspace you are in, and — when you belong to more than one — the
 * way to change it.
 *
 * Switching is the same `selectBusiness` CenoX Business uses: every query is
 * keyed by workspace, so the page refetches for the new one. An open thread
 * belongs to the workspace it was started in, so Chat returns to its list.
 */
function WorkspaceSwitcher({ onNavigate }: { onNavigate?: () => void }): ReactNode {
  const { user, business, memberships = [], selectBusiness } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const many = memberships.length > 1;

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent): void => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const face = (
    <>
      <span className="bg-cx-gradient flex size-8 shrink-0 items-center justify-center rounded-lg text-xs font-semibold text-white">
        {initials(business?.businessName ?? user?.fullName ?? '?')}
      </span>
      <span className="min-w-0 flex-1 text-left">
        {/*
          No workspace is a normal state, not a broken one: a CenoX account
          exists before any workspace does.
        */}
        <span className="block truncate text-sm font-medium text-white">
          {business?.businessName ?? user?.fullName ?? 'CenoX'}
        </span>
        <span className="block truncate text-xs capitalize text-navy-300">
          {business?.role ?? 'Personal account'}
        </span>
      </span>
    </>
  );

  if (!many) return <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">{face}</div>;

  return (
    <div
      ref={box}
      className="relative"
      onKeyDown={(e) => {
        // Handled here, before the drawer's own Escape, so only the list closes.
        if (e.key === 'Escape' && open) {
          e.preventDefault();
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Switch workspace"
        className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-navy-800 focus-visible:outline-2 focus-visible:outline-brand-400"
      >
        {face}
        <ChevronDown className={cn('size-4 shrink-0 text-navy-300 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Workspaces"
          className="absolute inset-x-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-xl border border-navy-700 bg-navy-800 p-1 shadow-xl"
        >
          {memberships.map((m) => {
            const current = m.businessId === business?.businessId;
            return (
              <li key={m.businessId}>
                <button
                  type="button"
                  role="option"
                  aria-selected={current}
                  onClick={() => {
                    setOpen(false);
                    if (current) return;
                    selectBusiness(m.businessId);
                    if (pathname.startsWith('/chat/')) navigate('/chat');
                    onNavigate?.();
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
                    current ? 'text-white' : 'text-navy-200 hover:bg-navy-700 hover:text-white',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{m.businessName}</span>
                    <span className="block truncate text-xs capitalize text-navy-400">{m.role}</span>
                  </span>
                  {current && <Check className="size-4 shrink-0 text-brand-300" aria-hidden />}
                </button>
              </li>
            );
          })}
          {/* The same onboarding every new business goes through; it owns nothing more than any other. */}
          {user?.kind === 'personal' && (
          <li className="mt-1 border-t border-navy-700 pt-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/business/onboarding');
                onNavigate?.();
              }}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-navy-200 transition-colors hover:bg-navy-700 hover:text-white"
            >
              <Plus className="size-4 shrink-0" aria-hidden />
              Add business
            </button>
          </li>
          )}
        </ul>
      )}
    </div>
  );
}

/** Who is signed in, and the two things you do with that: manage it, or leave. */
function AccountFooter({ onNavigate }: { onNavigate?: () => void }): ReactNode {
  const { user, business, logout } = useAuth();

  return (
    <div className="border-t border-navy-800 p-3">
      <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
        <Link
          to="/account"
          onClick={onNavigate}
          aria-label="Account"
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg focus-visible:outline-2 focus-visible:outline-brand-400"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-navy-700 text-xs font-semibold text-white">
            {initials(user?.fullName ?? '?')}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-white">{user?.fullName}</span>
            <span className="block truncate text-xs text-navy-300">
              {business?.businessName ?? user?.email}
            </span>
          </span>
        </Link>
        <button
          type="button"
          onClick={() => void logout()}
          aria-label="Sign out"
          title="Sign out"
          className="rounded-lg p-1.5 text-navy-300 transition-colors hover:bg-navy-800 hover:text-white"
        >
          <LogOut className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}

function SidebarBody({ onNavigate }: { onNavigate?: () => void }): ReactNode {
  const { pathname, search } = useLocation();
  const products = usePlatformProducts();
  const { business } = useAuth();
  const platform = usePlatformMe();
  /*
    Offered inside the CenoX company workspace, to a platform admin only. The
    role decides; the workspace only decides where the link sits. Until a
    company workspace is named, an admin sees it everywhere so they can name one.
  */
  const platformAdmin =
    platform.platformAdmin && (platform.companyBusinessId === null || platform.companyBusinessId === business?.businessId);
  const [ecosystemOpen, setEcosystemOpen] = useState(true);

  // Everything except CenoX AI — its sections are the nav above.
  const ecosystem = (products.data?.data ?? []).filter((p) => p.product !== 'ai');

  return (
    <>
      <div className="flex h-14 shrink-0 items-center gap-2 px-4">
        <Link to="/home" onClick={onNavigate} className="flex items-center gap-2 rounded-lg focus-visible:outline-2 focus-visible:outline-brand-400">
          <CenoxMark className="size-6" />
          <span className="text-base font-semibold tracking-tight text-white">
            Ceno
            <span className="bg-gradient-to-r from-blue-400 to-fuchsia-400 bg-clip-text text-transparent">X</span>
          </span>
        </Link>
      </div>

      <div className="px-3 pb-3">
        <WorkspaceSwitcher onNavigate={onNavigate} />
      </div>

      <nav
        className="flex-1 overflow-y-auto px-3 pb-8 [mask-image:linear-gradient(to_bottom,black_calc(100%-2rem),transparent)]"
        aria-label="CenoX"
      >
        <div className="space-y-0.5">
          {NAV.map((item) => {
            const active = item.active(pathname, search);
            return (
              /*
                Link, not NavLink: NavLink marks itself current from the path
                alone, which would announce both Chat and Work on `/chat`.
                The shell decides, from the path and the mode.
              */
              <Link
                key={item.label}
                to={item.to}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
                className={navItem(active)}
              >
                <item.icon className="size-4 shrink-0" aria-hidden />
                {item.label}
              </Link>
            );
          })}
        </div>

        {platformAdmin && (
          <NavLink
            to="/platform-admin"
            onClick={onNavigate}
            className={({ isActive }) => cn('mt-1', navItem(isActive))}
          >
            <Gauge className="size-4 shrink-0" aria-hidden />
            Platform Admin
          </NavLink>
        )}

        <div className="mt-4 border-t border-navy-800 pt-3">
          <button
            type="button"
            onClick={() => setEcosystemOpen((v) => !v)}
            aria-expanded={ecosystemOpen}
            className="flex w-full items-center justify-between rounded px-2.5 py-1 text-xs font-medium uppercase tracking-wide text-navy-400 hover:text-navy-200"
          >
            CenoX Ecosystem
            <ChevronDown className={cn('size-3.5 transition-transform', !ecosystemOpen && '-rotate-90')} />
          </button>
          {ecosystemOpen && (
            <div className="mt-1">
              {ecosystem.map((p) => (
                <EcosystemRow key={p.product} product={p} onNavigate={onNavigate} />
              ))}
              {products.isError && (
                <p className="px-2.5 py-2 text-xs text-navy-400">Could not load your products.</p>
              )}
            </div>
          )}
        </div>
      </nav>

      <AccountFooter onNavigate={onNavigate} />
    </>
  );
}

/** Connected to CenoX, and Recent activity. */
function RightPanel(): ReactNode {
  const { business, can } = useAuth();
  const products = usePlatformProducts();

  /*
    Only what this workspace holds and has set up — the SAME call the sidebar
    reads, so the two cannot disagree. A locked product here would be a door
    that does not open, and anything derived from its data would be worse.
  */
  const connected = (products.data?.data ?? []).filter(
    (p) => p.product !== 'ai' && p.availableAction === 'open',
  );
  const aiReady =
    products.data?.data.find((p) => p.product === 'ai')?.availableAction === 'open' && can('ai:use');
  const activity = useAiConversations(aiReady);

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Connected to CenoX</h2>
          <Link to="/home" className="text-xs text-brand-600 hover:underline">
            View all
          </Link>
        </div>
        {connected.length === 0 ? (
          <p className="py-2 text-sm text-slate-500">
            {business
              ? 'Nothing else is set up in this workspace yet.'
              : 'No CenoX products are set up for your account yet.'}
          </p>
        ) : (
          <div className="space-y-1">
            {connected.map((p) => (
              <Link
                key={p.product}
                to={`/${p.routeSlug}`}
                className="flex items-start gap-2.5 rounded-lg px-2 py-2 hover:bg-slate-50"
              >
                <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 text-white">
                  <Boxes className="size-4" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900">{p.name}</span>
                  <span className="block truncate text-xs text-slate-500">{p.description}</span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/*
        Recent activity, honestly: conversations are the one thing CenoX AI
        records, so that is what is shown — and nothing at all where the
        workspace does not hold CenoX AI.
      */}
      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <h2 className="mb-1 text-sm font-semibold text-slate-900">Recent activity</h2>
        <ActivityList rows={activity.data?.data ?? []} limit={4} />
      </section>
    </div>
  );
}

const headerButton =
  'rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-brand-500';

export function CenoxShell({
  children,
  rail = true,
}: {
  children: ReactNode;
  /**
   * The docked right panel, from xl. Home and Chat turn it off; the same panel
   * still opens as a sheet from the header's products button.
   */
  rail?: boolean;
}): ReactNode {
  const { user } = useAuth();
  const [drawer, setDrawer] = useState(false);
  const [panel, setPanel] = useState(false);
  const location = useLocation();
  const theme = useCenoxTheme();
  const ThemeIcon = THEME_ICON[theme.preference];
  const drawerBox = useRef<HTMLDivElement>(null);
  const panelBox = useRef<HTMLDivElement>(null);
  useOverlayFocus(drawer, drawerBox, () => setDrawer(false));
  useOverlayFocus(panel, panelBox, () => setPanel(false));

  // A drawer that survives navigation hides the page you just asked for.
  useEffect(() => {
    setDrawer(false);
    setPanel(false);
  }, [location.pathname, location.search]);

  return (
    <div
      data-theme={theme.dark ? 'dark' : 'light'}
      className={cn('min-h-dvh bg-slate-50 text-slate-900 lg:flex', theme.dark && 'cx-dark')}
    >
      {/* ── Sidebar: permanent from lg, a drawer below it ─────────────── */}
      <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col bg-navy-900 lg:flex">
        <SidebarBody />
      </aside>

      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close menu"
            tabIndex={-1}
            onClick={() => setDrawer(false)}
            className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
          />
          <div
            ref={drawerBox}
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-navy-900 shadow-2xl"
          >
            <SidebarBody onNavigate={() => setDrawer(false)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* ── Top bar ───────────────────────────────────────────────── */}
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-slate-200 bg-white/90 px-3 backdrop-blur sm:px-4">
          <button
            type="button"
            onClick={() => setDrawer(true)}
            aria-label="Open menu"
            className={cn(headerButton, 'lg:hidden')}
          >
            <Menu className="size-5" />
          </button>

          <Link to="/home" className="flex items-center gap-1.5 lg:hidden">
            <CenoxMark className="size-6" />
            <span className="text-sm font-semibold text-slate-900">CenoX</span>
          </Link>

          {/*
            Global search.

            Nothing searchable exists yet — no projects, no agents, no files —
            so it is visibly inert rather than a box that swallows what you
            type and answers nothing.
          */}
          <div
            title="Search is not built yet"
            className="ml-auto hidden min-w-0 max-w-xl flex-1 cursor-not-allowed items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 md:flex lg:ml-0"
          >
            <Search className="size-4 shrink-0 text-slate-400" aria-hidden />
            <span className="truncate text-sm text-slate-400">
              Search across projects, agents, files, prompts, models…
            </span>
          </div>

          <div className="ml-auto flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => setPanel(true)}
              aria-label="Connected to CenoX"
              title="Connected to CenoX"
              className={cn(headerButton, rail && 'xl:hidden')}
            >
              <Boxes className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => theme.setPreference(NEXT_THEME[theme.preference])}
              aria-label={THEME_LABEL[theme.preference]}
              title={THEME_LABEL[theme.preference]}
              className={headerButton}
            >
              <ThemeIcon className="size-5" />
            </button>
            <button type="button" aria-label="Notifications" title="Nothing new" className={headerButton}>
              <Bell className="size-5" />
            </button>
            <Link
              to="/account"
              aria-label="Your account"
              title={user?.fullName}
              className="ml-1 hidden size-8 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700 transition-shadow hover:ring-2 hover:ring-brand-200 focus-visible:outline-2 focus-visible:outline-brand-500 sm:flex"
            >
              {initials(user?.fullName ?? '?')}
            </Link>
          </div>
        </header>

        <div className="flex min-w-0 flex-1 flex-col xl:flex-row">
          <main id="main" className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
            {children}
          </main>

          {rail && (
            <aside className="hidden w-80 shrink-0 px-4 py-6 xl:block">
              <RightPanel />
            </aside>
          )}
        </div>
      </div>

      {/* ── Connected to CenoX as a sheet ────────────────────────────── */}
      {panel && (
        <div className={cn('fixed inset-0 z-50', rail && 'xl:hidden')}>
          <button
            type="button"
            aria-label="Close"
            tabIndex={-1}
            onClick={() => setPanel(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
          />
          <div
            ref={panelBox}
            role="dialog"
            aria-modal="true"
            aria-label="Connected to CenoX"
            className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-slate-50 p-4 sm:inset-x-auto sm:inset-y-0 sm:right-0 sm:max-h-none sm:w-96 sm:rounded-none sm:rounded-l-2xl"
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">Connected to CenoX</h2>
              <button
                type="button"
                onClick={() => setPanel(false)}
                aria-label="Close"
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200"
              >
                <X className="size-5" />
              </button>
            </div>
            <RightPanel />
          </div>
        </div>
      )}
    </div>
  );
}
